import classNames from 'clsx';
import { range } from 'lodash';
import type { ReactNode } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import type { RowProps } from '@swf/casino';
import { LeaderboardCollapsible, LeaderboardRow } from '@swf/casino';
import CurrencySymbol from '@swf/components/CurrencySymbol/CurrencySymbol';
import Translation from '@swf/components/Translation/Translation';
import FSBIcon from '@swf/core/assets/casino/free-spins.png';
import ibIcon from '@swf/core/assets/casino/instant-bonus.png';
import { Currency } from '@swf/core/enums';
import useRequest from '@swf/core/hooks/useRequest';
import { select } from '@swf/core/store';
import type { CasinoRace, Prize, PrizeDetail, Standing, Status } from '@swf/core/types/CasinoRaces';
import { PrizeType } from '@swf/core/types/CasinoRaces';

import rowStyles from '../LeaderboardRow/LeaderboardRow.scss';

import { TXTCLI_Prize, TXTCLI_Rank, TXTCLI_Score } from './Leaderboard.messages.json';
import styles from './Leaderboard.scss';

interface Props {
  className?: string;
  race: CasinoRace;
  standings: Standing[] & Prize[];
  medal?: boolean;
  usePosition?: boolean;
  leaderboard: boolean;
  status?: Status;
}

enum YouSequence {
  DoNothing,
  NeedExpand,
  NeedScroll
}

const currency = (prizeDetails: PrizeDetail) => (
  <th
    className={classNames(styles.prizes, {
      [styles.ibPad]: prizeDetails.prizeType === PrizeType.IB,
      [styles.fsbPad]: prizeDetails.prizeType === PrizeType.FSB,
      [styles.cashPad]: [PrizeType.CASH, PrizeType.NONE].includes(prizeDetails.prizeType)
    })}
  >
    <Translation tagName="div" {...TXTCLI_Prize} />
    <div
      className={classNames({
        [styles.currency]: prizeDetails.prizeType !== PrizeType.NONE
      })}
    >
      {prizeDetails.prizeType === PrizeType.IB && (
        <img className={styles.ibIcon} src={ibIcon} alt="" />
      )}
      {prizeDetails.prizeType === PrizeType.FSB && (
        <img className={styles.fsbIcon} src={FSBIcon} alt="" />
      )}
      {prizeDetails.prizeType === PrizeType.CASH && (
        <>
          (
          <CurrencySymbol
            currency={prizeDetails ? prizeDetails.currency : Currency.USDollar}
            trimSpace
          />
          )
        </>
      )}
    </div>
  </th>
);

const renderRows = (standings: Standing[], { userStanding, onYouVisible, containment }: RowProps) =>
  standings.map((standing, index) => (
    <LeaderboardRow
      index={index}
      medal
      standing={standing}
      userStanding={userStanding}
      key={standing.rank}
      onYouVisible={onYouVisible}
      containment={containment}
      leaderboard
      grouped
      isMobile
    />
  ));

// TODO: Refactor into the component. We discussed this +100500 times already
// Where the types?
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const renderPaged: (props: any) => ReactNode = ({
  prizeDetails,
  standings,
  activeList = [] as Props['standings'],
  expandedRange,
  onExpandRowClick,
  rowProps
}) => {
  const pageCount = Math.ceil(prizeDetails.numPrizePositions / standings.length);
  const pageSize = standings.length;

  return range(0, pageCount).map((index) => (
    <React.Fragment key={index}>
      <LeaderboardCollapsible
        key={index}
        index={index}
        start={index * pageSize + 1}
        onClick={() => onExpandRowClick(index)}
        end={index === pageCount - 1 ? prizeDetails.numPrizePositions : (index + 1) * pageSize}
        isExpanded={expandedRange === index}
      />
      {expandedRange === index && renderRows(activeList, { ...rowProps, grouped: true })}
    </React.Fragment>
  ));
};

const directScroll = () => {
  const youNode = document.querySelector(`.${rowStyles.current}`);
  if (youNode) {
    youNode.scrollIntoView();
  }
};

const Leaderboard: React.FC<Props> = ({
  className = '',
  standings,
  race,
  medal = true,
  leaderboard
}) => {
  const { prizeDetails, userRaceStanding, status, id: raceId } = race;
  const [isYouVisible, setYouVisible] = useState(false);
  const [expandedRange, setExpandedRange] = useState(0);
  const [activeList, setActiveList] = useState(standings);
  const [youStage, setYouStage] = useState(YouSequence.DoNothing);
  const isMobile = useSelector(select.device.isMobile);
  const isAuthenticated = useSelector(select.session.isAuthenticated);
  const scrollerRef = useRef<HTMLTableSectionElement>(null);
  const usePaging = isAuthenticated && prizeDetails.numPrizePositions > standings.length;
  const request = useRequest();

  const onExpandRowClick = async (page: number) => {
    setActiveList([]);
    if (expandedRange !== page) {
      setExpandedRange(page);
      request({
        service: 'casino.races.raceleaderboard',
        body: { page, raceId, status }
      }).subscribe(
        ({ body }) => setActiveList(body),
        () => setActiveList([])
      );
    } else {
      setExpandedRange(-1);
      setYouVisible(false);
    }
  };

  const scrollToYou = () => {
    const { rank, userRacePrize } = userRaceStanding || { rank: 0, userRacePrize: { amount: 0 } };
    if (userRacePrize.amount <= 0) {
      return;
    }
    const rankPage = Math.floor(rank / standings.length);
    if (rankPage !== expandedRange) {
      setYouStage(YouSequence.NeedExpand);
      onExpandRowClick(rankPage);
    } else {
      directScroll();
    }
  };

  useEffect(() => {
    setActiveList(standings);
  }, [standings]);

  useEffect(() => {
    if (youStage === YouSequence.NeedScroll) {
      setYouStage(YouSequence.DoNothing);
      directScroll();
    }
    if (youStage === YouSequence.NeedExpand && activeList.length > 0) {
      setYouStage(YouSequence.NeedScroll);
    }
  }, [activeList, youStage]);

  if (!standings || standings.length === 0) {
    return null;
  }

  const rowProps = {
    index: 0,
    standing: {} as Standing,
    onYouVisible: setYouVisible,
    medal,
    userStanding: userRaceStanding,
    containment: scrollerRef.current,
    leaderboard,
    isMobile
  };

  return (
    <div className={classNames(styles.legacy, styles.container)}>
      <table className={classNames(className)}>
        <thead>
          <tr>
            <th className={classNames({ [styles.mobile]: isMobile })} />
            <th>
              <Translation {...TXTCLI_Rank} />
            </th>
            {leaderboard && <Translation tagName="th" {...TXTCLI_Score} />}
            {currency(prizeDetails)}
          </tr>
        </thead>
        <tbody ref={scrollerRef}>
          {usePaging
            ? renderPaged({
                standings,
                expandedRange,
                onExpandRowClick,
                activeList,
                rowProps,
                prizeDetails: race.prizeDetails
              })
            : renderRows(standings, { ...rowProps })}

          {userRaceStanding && userRaceStanding.rank > 0 && !isYouVisible && (
            <LeaderboardRow
              index={standings.length}
              standing={userRaceStanding}
              key="you"
              medal={medal}
              onClick={() => scrollToYou()}
              userStanding={userRaceStanding}
              leaderboard={leaderboard}
              isMobile
              floatingYou
            />
          )}
        </tbody>
      </table>
    </div>
  );
};

Leaderboard.displayName = 'Leaderboard';

export default Leaderboard;
